/**
 * AdminEditor — article-edit 區塊式視覺編輯器（零依賴 / demo 級，不接後端）
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（無 [data-editor] DOM 即 no-op）。
 *
 * Path A 零依賴：用 contenteditable + 原生 Selection / Range + document.execCommand
 * 做行內格式切換（粗體 / 斜體 / 底線 / 刪除線），不引入任何第三方 editor lib。
 * execCommand 雖標記 deprecated，但仍為全瀏覽器可用、唯一不需數百行 Range 手術即可
 * 跨「部分選取」toggle 行內樣式的零依賴途徑；本元件為 demo 編輯器，足夠。
 *
 * 設計對齊 cr-form-card.js / admin-table.js：
 *   WeakMap 實例快取 + class 實例 + data-* 宣告式 auto-init + escapeHtml 防 XSS。
 *   無 [data-editor] DOM 時 no-op（不 throw）。
 *
 * 宣告式（auto-init via [data-editor]）：
 *   <div class="editor" data-editor>
 *     <div class="editor__toolbar" role="toolbar" aria-label="編輯工具列">
 *       <button type="button" class="editor__tool" data-editor-cmd="bold"      aria-label="粗體"   aria-pressed="false"><b>B</b></button>
 *       <button type="button" class="editor__tool" data-editor-cmd="italic"    aria-label="斜體"   aria-pressed="false"><i>I</i></button>
 *       <button type="button" class="editor__tool" data-editor-cmd="underline" aria-label="底線"   aria-pressed="false"><u>U</u></button>
 *       <button type="button" class="editor__tool" data-editor-cmd="strike"    aria-label="刪除線" aria-pressed="false"><s>S</s></button>
 *       <button type="button" class="editor__tool" data-editor-cmd="h2"        aria-label="大標題">H2</button>
 *       <button type="button" class="editor__tool" data-editor-cmd="h3"        aria-label="小標題">H3</button>
 *       <button type="button" class="editor__tool" data-editor-cmd="quote"     aria-label="引用">❝</button>
 *       <button type="button" class="editor__tool" data-editor-cmd="ul"        aria-label="項目清單">•</button>
 *       <button type="button" class="editor__tool" data-editor-cmd="ol"        aria-label="編號清單">1.</button>
 *       <button type="button" class="editor__tool" data-editor-cmd="link"      aria-label="插入連結">🔗</button>
 *       <!-- 插入區塊範本 -->
 *       <button type="button" class="editor__tool" data-editor-block="img-left"  aria-label="左圖右文">▤</button>
 *       <button type="button" class="editor__tool" data-editor-block="img-right" aria-label="右圖左文">▥</button>
 *       <button type="button" class="editor__tool" data-editor-block="img-full"  aria-label="單圖">▦</button>
 *       <button type="button" class="editor__tool" data-editor-block="img-pair"  aria-label="雙圖">▧</button>
 *       <button type="button" class="editor__tool" data-editor-block="text"      aria-label="純文字段">¶</button>
 *       <!-- HTML 原始碼模式切換 -->
 *       <button type="button" class="editor__tool editor__tool--toggle" data-editor-cmd="html" aria-label="切換 HTML 原始碼" aria-pressed="false">&lt;/&gt;</button>
 *     </div>
 *     <div class="editor__surface" data-editor-surface contenteditable="true" role="textbox"
 *          aria-multiline="true" aria-label="文章內容"></div>
 *     <textarea class="editor__source" data-editor-source hidden aria-label="HTML 原始碼"></textarea>
 *     <!-- 可選：把內容同步進此 hidden input 供表單送出 -->
 *     <input type="hidden" name="content" data-editor-output />
 *   </div>
 *
 * 程式式 API：
 *   import { AdminEditor } from './components/admin-editor.js';
 *   const ed = AdminEditor.get('#my-editor');
 *   ed.getHTML(); ed.setHTML('<p>…</p>');
 */

// XSS 防護：data 字串進 template 前 escape（共用 util，對齊 render-cards.js / admin-table.js；下方 AdminEditor.escapeHtml 仍對外暴露同一份）
import { escapeHtml } from '../utils/dom.js';

const instances = new WeakMap(); // editor root -> AdminEditorInstance
let insertSeq = 0; // insertBlock() 每次插入的遞增序號，供暫時 marker 屬性定位剛插入的節點（見 insertBlock）

/** P1-8 roving toolbar：把一條 toolbar 的 tab stop 重設為「第一顆未 disabled 鈕 = 0，其餘 -1」。 */
function resetToolbarTabStop(toolbar) {
  const all = Array.from(toolbar.querySelectorAll('button'));
  all.forEach((b) => { b.tabIndex = -1; });
  const firstEnabled = all.find((b) => !b.disabled);
  if (firstEnabled) firstEnabled.tabIndex = 0;
}

/* ---------- 行內格式：data-editor-cmd → execCommand 對應 ---------- */
// strike 在不同瀏覽器用 strikeThrough；其餘同名。queryCommandState 用同一 key 查 active。
const INLINE_CMD = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strike: 'strikeThrough',
};

// 區塊級 formatBlock 對應（標題 / 引用）
const BLOCK_CMD = {
  h2: 'h2',
  h3: 'h3',
  quote: 'blockquote',
};

// 清單
const LIST_CMD = {
  ul: 'insertUnorderedList',
  ol: 'insertOrderedList',
};

/* ---------- 5 種區塊範本 ---------- */
// 佔位圖 class 對齊 admin-editor.css 既有 .editor__block-media 規則（虛線框 + aspect-ratio
// 3:2 + 置中 icon，同頁「情境 B」種子區塊已用同一套 class+icon，此處照抄同一 SVG path，
// 非另立新視覺）；先前誤用 .editor__block-img（CSS 無對應規則）且無 icon 子節點，導致占位
// 塌陷成 0×0 不可見（回報 證據4）。alt 留空字串待編者補（demo 不寫死 alt 文案）。
// contenteditable=false 鎖住結構，內文區另開可編輯。
const PLACEHOLDER_IMG = '<span class="editor__block-media" data-editor-ph contenteditable="false" role="img" aria-label="圖片占位，點擊上方工具更換"><svg viewBox="0 0 24 24" fill="none" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg></span>';
const PLACEHOLDER_TEXT = '在此輸入內文…';

// marker（選填）：插入瞬間在 wrapper 掛一個暫時性 data attribute，供 insertBlock() 插入後
// 精準定位剛插入的節點以修正 caret 落點（見 insertBlock 內說明）；定位完成即移除，不進最終輸出 HTML。
//
// wrapper modifier class 對齊 admin-editor.css 已定義 / 已記載的版型命名（該檔案頭註解與
// §4 小節註解皆已載明 -img-text-l/-r/-single/-double/-text 這套命名）：
//   img-text-l / img-text-r ← 兩欄圖文，CSS 已有 grid 規則（含 ≥768 右圖左文 order:2 鏡像）
//   double                  ← 雙圖並排，CSS 已有 grid 規則
//   single / text           ← 無需獨立 grid，沿用 .editor__block 基礎樣式即可
// 舊版 wrapper 誤用 -img-left/-img-right/-img-full/-img-pair（CSS 端零規則命中，回報 證據1/3），
// 插入的區塊因此 fallback 成裸的 .editor__block（無 grid、無鏡像），此為版型切換故障主因。
// data-block 屬性（= kind 原值）維持不變，其他頁面（如 platform-privacy.html）以 kind 值
// 呼叫本函式，不受 wrapper class 命名調整影響。
function blockTemplate(kind, marker) {
  const markerAttr = marker ? ` data-editor-insert-marker="${marker}"` : '';
  const text = `<div class="editor__block-text"><p>${PLACEHOLDER_TEXT}</p></div>`;
  switch (kind) {
    case 'img-left':
      return `<div class="editor__block editor__block--img-text-l" data-block="img-left"${markerAttr}>${PLACEHOLDER_IMG}${text}</div>`;
    case 'img-right':
      return `<div class="editor__block editor__block--img-text-r" data-block="img-right"${markerAttr}>${text}${PLACEHOLDER_IMG}</div>`;
    case 'img-full':
      return `<div class="editor__block editor__block--single" data-block="img-full"${markerAttr}>${PLACEHOLDER_IMG}</div>`;
    case 'img-pair':
      return `<div class="editor__block editor__block--double" data-block="img-pair"${markerAttr}>${PLACEHOLDER_IMG}${PLACEHOLDER_IMG}</div>`;
    case 'text':
    default:
      return `<div class="editor__block editor__block--text" data-block="text"${markerAttr}>${text}</div>`;
  }
}

class AdminEditorInstance {
  constructor(el) {
    this.el = el;
    this.surface = el.querySelector('[data-editor-surface]');
    this.source = el.querySelector('[data-editor-source]');   // optional textarea（HTML 模式）
    this.output = el.querySelector('[data-editor-output]');   // optional hidden input（表單送出）
    // 編輯器可有多條 role="toolbar"（格式列 + 插入區塊列），事件委派掛 this.el；
    // P1-8 roving tabindex 逐條套。
    this.toolbars = Array.from(el.querySelectorAll('.editor__toolbar[role="toolbar"]'));
    this.tools = Array.from(el.querySelectorAll('[data-editor-cmd], [data-editor-block]'));
    // P1-6 連結插入 Modal：連結鈕若帶 data-modal-trigger，改走 Modal（取代 prompt 雙軌）。
    this.linkBtn = el.querySelector('[data-editor-cmd="link"]');
    this.linkModalSel = this.linkBtn ? this.linkBtn.getAttribute('data-modal-trigger') : null;
    this.isHtmlMode = false;
    this.savedRange = null; // 點工具列時 surface 失焦前的選取

    if (!this.surface) return; // 無編輯區即不啟用（no-op）

    // 空 surface 給一個起始段落，確保 caret 有落點 + execCommand 區塊指令有對象
    if (!this.surface.innerHTML.trim()) {
      this.surface.innerHTML = '<p><br></p>';
    }

    this._bind();
    this._syncOutput();
  }

  _bind() {
    // 記住 surface 內最後選取（mousedown 工具列會奪焦，導致 selection 塌陷）
    const save = () => this._saveRange();
    this.surface.addEventListener('keyup', save);
    this.surface.addEventListener('mouseup', save);
    this.surface.addEventListener('focus', save);
    document.addEventListener('selectionchange', () => {
      if (document.activeElement === this.surface) {
        this._saveRange();
        this._syncToolStates();
      }
    });

    // 工具列：mousedown 階段 preventDefault，避免按鈕奪走 surface 焦點 → 選取消失。
    // 委派掛 editor 根（this.el）而非單一 .editor__toolbar：編輯器有「格式列 + 插入區塊列」
    // 兩條 sibling toolbar，掛根才能同時接到兩列的鈕（含插入區塊鈕，舊版只掛第一列接不到）。
    this.el.addEventListener('mousedown', (e) => {
      if (e.target.closest('[data-editor-cmd], [data-editor-block]')) e.preventDefault();
    });

    this.el.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-editor-cmd], [data-editor-block]');
      if (!btn || !this.el.contains(btn)) return;
      // checkbox 型工具（HTML 模式 switch，見 toggleHtmlMode）不可 preventDefault：
      // click 被 cancel 時，瀏覽器會在「canceled activation steps」把 handler 剛設好的
      // .checked 回退（發生在本 handler 執行完之後），導致 aria-checked=true 但 checked=false、
      // :checked 選擇器永不成立、thumb 視覺凍結。讓原生切換正常完成即可修復，其餘工具鈕
      // （<button type="button">，無原生 activation 行為）維持攔截慣例不變。
      const isCheckboxToggle = btn.matches('input[type="checkbox"]');
      if (!isCheckboxToggle) e.preventDefault();
      if (btn.dataset.editorBlock) {
        this.insertBlock(btn.dataset.editorBlock);
      } else {
        this._runCmd(btn.dataset.editorCmd, btn);
      }
    });

    // P1-8 WAI-ARIA toolbar：每條 role="toolbar" 套 roving tabindex + 方向鍵巡覽。
    this.toolbars.forEach((tb) => this._initRovingToolbar(tb));

    // P1-6 連結插入 Modal 接線（連結鈕帶 data-modal-trigger 時）。
    this._initLinkModal();

    // P0-11 dirty 離開提醒（僅「不在 <form> 內」的獨立編輯器，由 data-dirty-modal 宣告啟用；
    // form 內編輯器（如 article-edit）由 admin-form-edit.js 統管，本段不重複攔截以免雙對話框）。
    this._initStandaloneDirty();

    // 內容變動 → 同步輸出（debounce 不需要，input 已批次）
    this.surface.addEventListener('input', () => this._syncOutput());

    // HTML 原始碼編輯 → 視覺即時跟（離開 textarea / 切回視覺時 commit）
    if (this.source) {
      this.source.addEventListener('input', () => {
        // 即時把原始碼灌回 surface（仍隱藏），切回時就已同步
        this.surface.innerHTML = this.source.value;
        this._syncOutput();
      });
    }
  }

  /* ----- selection / range ----- */
  _saveRange() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && this.surface.contains(sel.anchorNode)) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  _restoreRange() {
    this.surface.focus();
    const sel = window.getSelection();
    if (this.savedRange && sel) {
      sel.removeAllRanges();
      sel.addRange(this.savedRange);
    }
  }

  /* ----- 指令分派 ----- */
  _runCmd(cmd, btn) {
    if (cmd === 'html') { this.toggleHtmlMode(); return; }
    if (this.isHtmlMode) return; // 原始碼模式下行內指令無意義

    // P1-6：連結鈕帶 data-modal-trigger → 走 Modal（modal.js 已在該鈕綁 click 開窗）。
    // 此處只負責「保住選取 + 預填顯示文字」，不再跑 prompt，避免 Modal/prompt 雙軌。
    if (cmd === 'link' && btn && btn.getAttribute('data-modal-trigger') && this._linkModalEl()) {
      this._saveRange();
      this._prefillLinkModal();
      return;
    }

    this._restoreRange();

    if (INLINE_CMD[cmd]) {
      document.execCommand(INLINE_CMD[cmd], false, null);
    } else if (BLOCK_CMD[cmd]) {
      // formatBlock 需要 <tag> 形式（部分舊瀏覽器）；toggle：已是該區塊則退回 <p>
      const current = this._currentBlockTag();
      const target = current === BLOCK_CMD[cmd] ? 'p' : BLOCK_CMD[cmd];
      document.execCommand('formatBlock', false, `<${target}>`);
    } else if (LIST_CMD[cmd]) {
      document.execCommand(LIST_CMD[cmd], false, null);
    } else if (cmd === 'link') {
      this._insertLink(); // 無 Modal 時的零依賴 fallback（prompt）
    }

    this._saveRange();
    this._syncToolStates();
    this._syncOutput();
  }

  _currentBlockTag() {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) return '';
    let node = sel.anchorNode;
    node = node.nodeType === 1 ? node : node.parentElement;
    while (node && node !== this.surface) {
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (['h2', 'h3', 'blockquote', 'p'].includes(tag)) return tag;
      node = node.parentElement;
    }
    return '';
  }

  /** 連結 URL 安全閘門：僅放行 http(s) / mailto，擋 javascript: 等 XSS scheme。
   *  通過回傳 true；不通過跳 toast 並回傳 false。Modal 與 prompt 兩路共用。 */
  _isSafeLinkUrl(url) {
    if (/^(https?:|mailto:)/i.test(url)) return true;
    if (window.AdminToast) {
      window.AdminToast.show({ type: 'error', title: '網址格式不支援', text: '僅接受 http / https / mailto' });
    }
    return false;
  }

  /** 在目前（已還原的）選取套用連結。text 有值＝改用指定顯示文字插入；
   *  否則：有選取用 createLink（保留選取文字），無選取以網址為文字。XSS 已由呼叫端先擋。 */
  _applyLink(url, text) {
    this._restoreRange();
    const sel = window.getSelection();
    const hasSelection = sel && !sel.isCollapsed;
    const label = (text || '').trim();
    if (hasSelection && !label) {
      document.execCommand('createLink', false, url);
    } else {
      const shown = label || url;
      document.execCommand('insertHTML', false,
        `<a href="${escapeHtml(url)}">${escapeHtml(shown)}</a>`);
    }
    this._saveRange();
    this._syncOutput();
  }

  /** 無 Modal 時的零依賴 fallback：prompt 取 URL。 */
  _insertLink() {
    const url = window.prompt('連結網址：', 'https://');
    if (!url || !this._isSafeLinkUrl(url)) return;
    this._applyLink(url);
  }

  /* ----- P1-6 連結插入 Modal ----- */
  _linkModalEl() {
    return this.linkModalSel ? document.querySelector(this.linkModalSel) : null;
  }

  /** 開窗前把目前選取文字預填到 Modal 的「顯示文字」欄、清空網址欄。 */
  _prefillLinkModal() {
    const modal = this._linkModalEl();
    if (!modal) return;
    const textInput = modal.querySelector('#link-text, [name="linkText"]');
    const urlInput = modal.querySelector('#link-url, [name="linkUrl"]');
    const sel = window.getSelection();
    const selText = sel && !sel.isCollapsed ? sel.toString() : '';
    if (textInput) textInput.value = selText;
    if (urlInput) urlInput.value = '';
  }

  /** 綁定 Modal 的「插入」鈕 + 網址欄 Enter → 取值、擋 XSS、插連結、關窗。 */
  _initLinkModal() {
    const modal = this._linkModalEl();
    if (!modal) return;
    if (modal.dataset.editorLinkBound === '1') return; // 同一 Modal 只綁一次
    modal.dataset.editorLinkBound = '1';

    const confirmBtn = modal.querySelector('[data-action="insert-link-confirm"]');
    const urlInput = modal.querySelector('#link-url, [name="linkUrl"]');

    const confirm = () => this._confirmLinkFromModal(modal);
    if (confirmBtn) confirmBtn.addEventListener('click', confirm);
    if (urlInput) {
      urlInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); confirm(); }
      });
    }
  }

  _confirmLinkFromModal(modal) {
    const textInput = modal.querySelector('#link-text, [name="linkText"]');
    const urlInput = modal.querySelector('#link-url, [name="linkUrl"]');
    const url = (urlInput ? urlInput.value : '').trim();
    if (!url || !this._isSafeLinkUrl(url)) return; // 不通過：不關窗，讓使用者修正
    this._applyLink(url, textInput ? textInput.value : '');
    if (window.Modal && typeof window.Modal.close === 'function') {
      window.Modal.close(this.linkModalSel);
    }
  }

  /* ----- 區塊範本插入 ----- */
  insertBlock(kind) {
    if (this.isHtmlMode) return;
    this._restoreRange();
    const marker = `ins-${Date.now()}-${++insertSeq}`;
    const html = blockTemplate(kind, marker);
    // 插在目前區塊「之後」較直覺；execCommand insertHTML 落在 caret 處即可，
    // 範本本身是 block 元素，瀏覽器會自動斷行。
    document.execCommand('insertHTML', false, html);
    // execCommand('insertHTML') 插入後，瀏覽器有時把 caret 留在新區塊內部的可編輯文字
    // 節點裡（而非緊接區塊之後），導致下一次插入巢狀插進剛插入的區塊內部（實測：右圖左文
    // 插入後接著點單張圖片，新區塊跑進右圖左文區塊內部，回報「巢狀插錯位置」bug）。用剛
    // 掛上的暫時 marker 精準找到這個節點，把 caret 明確挪到它之後，確保連續插入時彼此是
    // canvas 同層 sibling 而非互相巢狀。marker 定位完即移除，不進最終輸出 HTML。
    const inserted = this.surface.querySelector(`[data-editor-insert-marker="${marker}"]`);
    if (inserted) {
      inserted.removeAttribute('data-editor-insert-marker');
      this._placeCaretAfter(inserted);
    } else {
      this._saveRange();
    }
    this._syncOutput();
    return this;
  }

  /** 把 caret 移到指定節點之後（collapsed range），並存入 savedRange。
   *  insertBlock() 插入後用來修正 caret 落點，避免下一次插入巢狀進剛插入的區塊。 */
  _placeCaretAfter(node) {
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    this._saveRange();
  }

  /* ----- P1-8 WAI-ARIA toolbar：roving tabindex + 方向鍵巡覽 ----- */
  /** 單一 toolbar 內：整列只有一個 tab stop（tabindex=0），其餘 -1；
   *  ← → ↑ ↓ 在鈕間移動，Home/End 跳首尾，跳過 disabled，環狀循環。
   *  焦點落在哪顆鈕，那顆即成新的 tab stop（focusin 同步）。 */
  _initRovingToolbar(toolbar) {
    const items = () =>
      Array.from(toolbar.querySelectorAll('button')).filter(
        (b) => !b.disabled && b.offsetParent !== null
      );

    const setTabStop = (target) => {
      Array.from(toolbar.querySelectorAll('button')).forEach((b) => {
        b.tabIndex = b === target ? 0 : -1;
      });
    };

    // 初始：第一顆可用鈕為 tab stop
    resetToolbarTabStop(toolbar);

    toolbar.addEventListener('keydown', (e) => {
      const list = items();
      if (!list.length) return;
      const cur = document.activeElement;
      let idx = list.indexOf(cur);
      if (idx === -1) return;

      let next;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = list[(idx + 1) % list.length];
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = list[(idx - 1 + list.length) % list.length];
          break;
        case 'Home':
          next = list[0];
          break;
        case 'End':
          next = list[list.length - 1];
          break;
        default:
          return;
      }
      e.preventDefault();
      setTabStop(next);
      next.focus();
    });

    // 鍵盤/滑鼠落焦到某鈕 → 該鈕成為唯一 tab stop（維持單一 tab stop 不變式）
    toolbar.addEventListener('focusin', (e) => {
      const btn = e.target.closest('button');
      if (btn && toolbar.contains(btn) && !btn.disabled) setTabStop(btn);
    });
  }

  /* ----- P0-11 dirty 離開提醒（獨立編輯器；form 內由 admin-form-edit.js 統管） ----- */
  _initStandaloneDirty() {
    // 在 <form> 內的編輯器交給 admin-form-edit.js，避免雙重 beforeunload / 雙對話框
    if (this.el.closest('form')) return;
    // 需頁面宣告捨棄確認 Modal 才啟用（data-dirty-modal="#discard-modal"）
    const modalSel = this.el.getAttribute('data-dirty-modal');
    if (!modalSel) return;
    this._dirty = false;

    const mark = () => { this._dirty = true; };

    // (1) 編輯器內容變動
    this.surface.addEventListener('input', mark);
    if (this.source) this.source.addEventListener('input', mark);

    // (2) 頁面其他欄位變動（排除 Modal 內、搜尋/篩選欄、明確 opt-out）
    const onFieldChange = (e) => {
      const t = e.target;
      if (!t || !t.matches('input, select, textarea')) return;
      if (t.type === 'search') return;            // 搜尋/篩選非內容編輯
      if (t.closest('.modal')) return;            // Modal 內互動不算 dirty
      if (t.closest('[data-no-dirty]')) return;   // 明確 opt-out
      mark();
    };
    document.addEventListener('input', onFieldChange);
    document.addEventListener('change', onFieldChange);

    // (3) 關分頁 / 重整：dirty 即攔（原生對話框，文案不可自訂）
    window.addEventListener('beforeunload', (e) => {
      if (this._dirty) { e.preventDefault(); e.returnValue = ''; }
    });

    // (4) 站內離開攔截：取消鈕 [data-confirm-discard] + 麵包屑 / 返回鈕。
    //     dirty → 開捨棄 Modal（Modal 內 danger <a href> 為真正離開）；非 dirty → 放行原生導航。
    const openDiscard = () => {
      if (window.Modal && typeof window.Modal.open === 'function') window.Modal.open(modalSel);
    };
    const navSel = '[data-confirm-discard], .admin-breadcrumb a[href], .admin-page-back a[href]';
    document.addEventListener('click', (e) => {
      const link = e.target.closest(navSel);
      if (!link) return;
      if (link.closest('.modal')) return; // Modal 內離開鈕不攔
      if (this._dirty) { e.preventDefault(); openDiscard(); }
    });
  }

  /* ----- 工具 active 態同步 ----- */
  _syncToolStates() {
    if (this.isHtmlMode) return;
    this.tools.forEach((btn) => {
      const cmd = btn.dataset.editorCmd;
      if (!cmd) return;
      let active;
      if (INLINE_CMD[cmd]) {
        try { active = document.queryCommandState(INLINE_CMD[cmd]); } catch (_) { active = false; }
      } else if (BLOCK_CMD[cmd]) {
        active = this._currentBlockTag() === BLOCK_CMD[cmd];
      } else if (LIST_CMD[cmd]) {
        try { active = document.queryCommandState(LIST_CMD[cmd]); } catch (_) { active = false; }
      } else {
        return; // link / html 不參與 active 同步（html 由 toggleHtmlMode 自管）
      }
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  /* ----- HTML 原始碼模式 ----- */
  toggleHtmlMode() {
    if (!this.source) return; // 無 textarea 不支援切換
    this.isHtmlMode = !this.isHtmlMode;
    const htmlBtn = this.el.querySelector('[data-editor-cmd="html"]');

    if (this.isHtmlMode) {
      // 視覺 → 原始碼：把 surface HTML 美化後填入 textarea
      this.source.value = this._prettyHtml(this.surface.innerHTML);
      this.source.hidden = false;
      this.surface.hidden = true;
      // 原始碼模式停用其他工具（避免對 textarea 套行內指令）
      this.tools.forEach((b) => { if (b.dataset.editorCmd !== 'html') b.disabled = true; });
      this.source.focus();
    } else {
      // 原始碼 → 視覺：textarea 內容回灌（_bind 的 input 已同步，這裡保險再灌一次）
      this.surface.innerHTML = this.source.value;
      this.source.hidden = true;
      this.surface.hidden = false;
      this.tools.forEach((b) => { b.disabled = false; });
      this.surface.focus();
      this._syncOutput();
    }
    // 工具 disabled 狀態變動後，重設每條 toolbar 的 roving tab stop（落在第一顆可用鈕）
    this._resetToolbarTabStops();

    this.el.classList.toggle('editor--html-mode', this.isHtmlMode);
    if (htmlBtn) {
      htmlBtn.classList.toggle('is-active', this.isHtmlMode);
      // 現行標記為 input[type=checkbox][role=switch]（article-edit.html / platform-privacy.html
      // 皆同款）：checkbox 帶顯式 aria-checked 後不隨 .checked 自動更新，需程式同步（鏡像
      // admin-table.js _syncSwitchAria 的既有慣例：「否則 SR 讀到落後狀態」）。委派 click
      // handler 已改為「checkbox 目標不 preventDefault」（見上方 _bind），原生切換行為得以
      // 正常完成，CSS .form-switch__input:checked 選擇器與 thumb 位移因而正常生效；這裡的
      // .checked 賦值降級為防禦性覆寫（確保與 isHtmlMode 內部旗標一致，涵蓋本方法被程式呼叫、
      // 非真實 click 觸發的邊界情況），不再是讓 CSS 生效的必要條件。非 checkbox（本檔
      // JSDoc 示範的 <button aria-pressed>）維持舊行為，向後相容。
      if ('checked' in htmlBtn) {
        htmlBtn.checked = this.isHtmlMode;
        htmlBtn.setAttribute('aria-checked', String(this.isHtmlMode));
      } else {
        htmlBtn.setAttribute('aria-pressed', String(this.isHtmlMode));
      }
    }
  }

  /** 重設每條 toolbar 的 roving tab stop（HTML 模式切換鈕 disabled 變動後呼叫）。 */
  _resetToolbarTabStops() {
    this.toolbars.forEach((tb) => resetToolbarTabStop(tb));
  }

  /** 極輕量縮排：在 block 級閉合標籤後補換行，純為可讀性（非完整 formatter） */
  _prettyHtml(html) {
    return html
      .replace(/></g, '>\n<')
      .replace(/\n{2,}/g, '\n')
      .trim();
  }

  /* ----- 輸出同步 ----- */
  _syncOutput() {
    if (this.output) this.output.value = this.getHTML();
  }

  getHTML() {
    return this.isHtmlMode && this.source ? this.source.value : this.surface.innerHTML;
  }

  setHTML(html) {
    if (this.surface) this.surface.innerHTML = html;
    if (this.isHtmlMode && this.source) this.source.value = this._prettyHtml(html);
    this._syncOutput();
    return this;
  }
}

function getOrCreate(target) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) return null;
  let inst = instances.get(el);
  if (!inst) {
    inst = new AdminEditorInstance(el);
    instances.set(el, inst);
  }
  return inst;
}

export const AdminEditor = {
  init(target) { return getOrCreate(target); },
  get(target) { return getOrCreate(target); },
  escapeHtml,
};

// ===== Auto-init =====
function bindAll(root = document) {
  root.querySelectorAll('[data-editor]').forEach((el) => {
    if (el.dataset.editorBound === '1') return;
    el.dataset.editorBound = '1';
    getOrCreate(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bindAll());
} else {
  bindAll();
}

if (typeof window !== 'undefined') {
  window.AdminEditor = AdminEditor;
}
